// Opt-in league team list for template chrome (Huddle's my-team
// picker, Arena's footer team bar). Lazy: nothing is fetched until a caller
// passes `enabled: true`, so classic (which never calls it) keeps its exact
// request order. One request per sport per session, shared by every caller
// (in-flight requests are de-duplicated); a sport switch refetches.
// #583: with 2+ live divisions in the sport the list is `/teams?division=all`
// (cached per sport + ':all') and each team carries `divisionIds`. Below that
// the request and the items are exactly today's.

import { useEffect, useState } from 'react'
import { api } from '@/services/api'
import type { Team } from '@/services/api/types'
import { useLeague } from '@/contexts/LeagueContext'
import { useSportContext } from '@/contexts/SportContext'
import { sectionEnabled } from '@/contexts/tenantAccess'
import { useSiteContentAllowed } from '../access/context'
import { useDivision } from '../division'

export interface SiteTeam {
  id: number
  name: string
  /** short_code, or null. */
  code: string | null
  logoUrl: string | null
  color: string | null
  city: string | null
  /** /teams/:id, or null when the Teams section is off. */
  href: string | null
  /**
   * #583: the live divisions whose public season has the team (a team can be
   * in two). Present only with 2+ live divisions in the sport.
   */
  divisionIds?: number[]
}

export interface SiteTeams {
  status: 'idle' | 'loading' | 'ready' | 'error'
  items: SiteTeam[]
}

type Entry = { promise: Promise<Team[]>; value?: Team[]; error?: unknown }
const cache = new Map<string, Entry>()

function load(sport: string, divisions: boolean): Entry {
  const key = divisions ? `${sport}:all` : sport
  let hit = cache.get(key)
  if (!hit) {
    const entry: Entry = {
      promise: api.getTeams(undefined, divisions ? { division: 'all' } : {}).then(
        (r) => { entry.value = r.teams ?? []; return entry.value },
        (e) => { entry.error = e; cache.delete(key); throw e },
      ),
    }
    cache.set(key, entry)
    hit = entry
  }
  return hit
}

/** Test hook: forget cached lists. */
export function resetSiteTeamsCache(): void {
  cache.clear()
}

export function useSiteTeams(wanted: boolean): SiteTeams {
  // Registration step 33: nothing until a members-only league's visitor is
  // admitted (always true on a public league).
  const allowed = useSiteContentAllowed()
  const enabled = wanted && allowed
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  const divisions = useDivision().enabled
  const sport = activeSportId || 'default'
  // The state key: the sport alone without divisions (today's), else sport + ':all'.
  const key = divisions ? `${sport}:all` : sport
  const [state, setState] = useState<{ sport: string; teams: Team[] | null; error: boolean }>({ sport: key, teams: null, error: false })

  useEffect(() => {
    if (!enabled) return
    let live = true
    const entry = load(sport, divisions)
    if (entry.value) setState({ sport: key, teams: entry.value, error: false })
    else entry.promise.then(
      (teams) => { if (live) setState({ sport: key, teams, error: false }) },
      () => { if (live) setState({ sport: key, teams: null, error: true }) },
    )
    return () => { live = false }
    // `key` is derived from sport and divisions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sport, divisions])

  if (!enabled) return { status: 'idle', items: [] }
  const current = state.sport === key ? state : { teams: null, error: false }
  if (current.error) return { status: 'error', items: [] }
  if (!current.teams) return { status: 'loading', items: [] }
  const linkTeams = sectionEnabled(league, 'teams')
  return {
    status: 'ready',
    items: current.teams.map((t) => ({
      id: t.id,
      name: t.name,
      code: t.short_code ?? null,
      logoUrl: t.logo_url ?? null,
      color: t.primary_color ?? null,
      city: t.city ?? null,
      href: linkTeams ? `/teams/${t.id}` : null,
      ...(divisions ? { divisionIds: t.division_ids ?? [] } : {}),
    })),
  }
}
